import {
  DEFAULT_REGION,
  LanguageCode,
  LanguageCodes,
  RegionCode,
  Regions,
} from '@ca/common'
import { DealType, DealTypeArray, DEFAULT_DEAL_TYPES } from '@ca/common/types'
import { APP_VERSION } from '@ca/common/version'
import Constants from 'expo-constants'
import { Platform } from 'react-native'
import { getApiUrl } from './getApiUrl'
import getEnvValue, {
  EnvKeys,
  getEnvBoolean,
  getParamOrEnv,
} from './getEnvValue'

const getParams = () => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    // Fallback to env var in case of expo will be EXPO_PUBLIC_PARAM_X and in managed app config PARAM_X
    return {}
  }
  const params = new URLSearchParams(window.location.search || '')
  const entries = params.entries()
  return Object.fromEntries(entries)
}

const checkDev = () => {
  const val = getParamOrEnv('PARAM_DEV')
  if (val == null) {
    return undefined
  }
  return val === '1' || val === 'true'
}

const isDev = checkDev() ?? Constants.debugMode

const getDebug = () => {
  const val = getParamOrEnv('PARAM_DEBUG')
  const opts = val?.split(',')
  const debug: Record<string, boolean> = {}
  if (!opts) {
    return debug
  }

  opts.forEach((opt) => {
    debug[opt] = true
  })
  return debug
}

export const getStaticRegion = (overrideCode?: RegionCode) => {
  const code =
    overrideCode ?? (getParamOrEnv('REGION', DEFAULT_REGION) as RegionCode)

  const reg = Regions[code]

  const defaultLanguage = getParamOrEnv('DEFAULT_LANG')
  const supportedLanguages = getParamOrEnv('UI_LANGS')?.split(',')

  if (defaultLanguage) {
    reg.languages.default = defaultLanguage as LanguageCode
  }
  if (supportedLanguages) {
    reg.languages.interface = supportedLanguages as LanguageCode[]
  }
  if (isDev) {
    reg.languages.interface.push('dev')
  }

  return reg
}

const getDealTypes = (): DealType[] => {
  const val = getParamOrEnv('DEAL_TYPES')
  if (!val) {
    return [...DEFAULT_DEAL_TYPES]
  }
  const types = val.split(',')

  if (types.length === 0) {
    return [...DEFAULT_DEAL_TYPES]
  }
  // Preserve order from the original array
  return DealTypeArray.filter((type) => types.includes(type))
}

const getDealLang = (): LanguageCode | null => {
  const dealLang = getParamOrEnv('DEAL_LANG')
  const isValid = LanguageCodes.includes(dealLang as LanguageCode)

  return isDev && isValid ? (dealLang as LanguageCode) : null
}

const getShowAdmin = () => {
  const value = getEnvValue('SHOW_ADMIN')
  if (value) {
    return value === '1' || value === 'true'
  }
  return isDev
}

const isAvatarEnabled = () => {
  const value = getParamOrEnv('AVATAR')
  if (value) {
    return value === '1' || value === 'true'
  }

  return false
}

/**
 * If not in dev mode, return the default value
 */
const getDevBoolean = (key: EnvKeys, defaultValue: boolean) => {
  if (!isDev) {
    return defaultValue
  }
  const value = getParamOrEnv(key)
  if (value == null) {
    return defaultValue
  }
  return value === '1' || value === 'true'
}

const AppConfig = {
  isDev,
  isDebug: Constants.debugMode,
  apiUrl: getApiUrl(),
  launchParams: getParams(),
  serialNumber: getParamOrEnv('SERIAL_NUMBER'),
  app: {
    version: APP_VERSION,
    hash: Constants.expoConfig?.extra?.build?.commit,
    date: Constants.expoConfig?.extra?.build?.date,
  },
  sentry: {
    enabled: !Constants.debugMode,
  },
  avatar: {
    enabled: isAvatarEnabled(),
  },
  i18n: {
    updateInterval: isDev ? 0 : 15 * 60 * 1000,
    staleTime: isDev ? 0 : 60 * 1000,
  },
  remoteConfig: {
    refetchInterval: 60 * 1000 * 5,
    state: isDev ? 'preview' : 'public',
    // We could use build number to drop the cached configs between builds
    version: 1,
  },
  isSdkEnabled: getDevBoolean(
    'EXPO_PUBLIC_FORCE_SDK_MOCK',
    Platform.select({ default: true, web: false })
  ),
  dealTypes: getDealTypes(),
  dealLang: getDealLang(),
  debug: getDebug(),
  showAdmin: getShowAdmin(),
  /**
   * Can change at runtime using admin toggle
   * @deprecated Do not use this directly, prefer useAppRegion or getAppRegion
   */
  staticRegion: getStaticRegion(),
  activity: {
    maxActivityAgeMs: 5 * 60 * 1000,
    logoutTimeoutSeconds: 30,
  },
  dev: {
    redirectsDisabled: getEnvBoolean('REDIRECTS_DISABLED'),
  },
} as const

console.log('AppConfig', AppConfig)

export default AppConfig
